import { useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function PremiumForm({ defaultTrack = "Medicine", testPrefix = "premium" }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    country: "",
    track: defaultTrack,
    academic_background: "",
    target_universities: "",
    timeline: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      await http.post("/leads/premium", form);
      setDone(true);
      toast.success("Premium request received — we'll reach out personally.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="card-premium text-center py-12" data-testid={`${testPrefix}-form-success`}>
        <CheckCircle2 className="w-12 h-12 text-[#D7141A] mx-auto mb-4" />
        <h3 className="font-serif text-2xl mb-2">Request received</h3>
        <p className="text-white/70">A senior advisor will reach out personally within 1–2 business days.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-premium" data-testid={`${testPrefix}-form`}>
      <div className="overline mb-2" style={{ color: "#E5444A" }}>Premium application strategy</div>
      <h3 className="font-serif text-2xl mb-6">Tell us about your profile</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label-sp" style={{ color: "#E6E1D4" }}>Full name *</label>
          <input className="input-sp bg-white/5 text-white border-white/20" style={{ background: "rgba(255,255,255,0.06)", color: "#fff" }} value={form.full_name} onChange={change("full_name")} required data-testid={`${testPrefix}-input-name`} />
        </div>
        <div>
          <label className="label-sp" style={{ color: "#E6E1D4" }}>Email *</label>
          <input className="input-sp" style={{ background: "rgba(255,255,255,0.06)", color: "#fff" }} type="email" value={form.email} onChange={change("email")} required data-testid={`${testPrefix}-input-email`} />
        </div>
        <div>
          <label className="label-sp" style={{ color: "#E6E1D4" }}>Phone / WhatsApp</label>
          <input className="input-sp" style={{ background: "rgba(255,255,255,0.06)", color: "#fff" }} value={form.phone} onChange={change("phone")} data-testid={`${testPrefix}-input-phone`} />
        </div>
        <div>
          <label className="label-sp" style={{ color: "#E6E1D4" }}>Country</label>
          <input className="input-sp" style={{ background: "rgba(255,255,255,0.06)", color: "#fff" }} value={form.country} onChange={change("country")} data-testid={`${testPrefix}-input-country`} />
        </div>
        <div>
          <label className="label-sp" style={{ color: "#E6E1D4" }}>Track</label>
          <select className="input-sp" style={{ background: "rgba(255,255,255,0.06)", color: "#fff" }} value={form.track} onChange={change("track")} data-testid={`${testPrefix}-input-track`}>
            {["Medicine","Dentistry","PhD"].map(l => <option key={l} style={{color: "#0D1B2A"}}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="label-sp" style={{ color: "#E6E1D4" }}>Timeline</label>
          <input className="input-sp" style={{ background: "rgba(255,255,255,0.06)", color: "#fff" }} placeholder="e.g. intake Oct 2026" value={form.timeline} onChange={change("timeline")} data-testid={`${testPrefix}-input-timeline`} />
        </div>
      </div>

      <div className="mt-4">
        <label className="label-sp" style={{ color: "#E6E1D4" }}>Academic background</label>
        <input className="input-sp" style={{ background: "rgba(255,255,255,0.06)", color: "#fff" }} value={form.academic_background} onChange={change("academic_background")} data-testid={`${testPrefix}-input-background`} />
      </div>

      <div className="mt-4">
        <label className="label-sp" style={{ color: "#E6E1D4" }}>Target universities / research focus</label>
        <textarea className="input-sp min-h-[90px]" style={{ background: "rgba(255,255,255,0.06)", color: "#fff" }} value={form.target_universities} onChange={change("target_universities")} data-testid={`${testPrefix}-input-targets`} />
      </div>

      <button type="submit" disabled={loading} className="btn-gold mt-6 w-full justify-center" data-testid={`${testPrefix}-submit`}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {loading ? "Sending…" : "Request Premium Guidance"}
      </button>
    </form>
  );
}
